import {generateLetterPdf} from '../utils/letterTypography';
import StatCards from './ui/StatCards';
import {toolsApi} from './ResearcherTools';
import { ApplicationFlags, InstitutionalFlag, roleLabel } from './ApplicationJourney';
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../contexts/AuthContext';
import './ApprovalInbox.css';

interface DocumentItem {
  id: number;
  documentType: string;
  originalFileName?: string | null;
  filePath?: string | null;
  downloadUrl?: string | null;
  fileSize?: number | null;
  contentType?: string | null;
  uploadedByUserId?: number | null;
  uploadedByUserName?: string | null;
  workflowStageCode?: string | null;
  workflowStageName?: string | null;
  applicationSection?: string | null;
  documentSource?: string | null;
  logicalDocumentKey?: string | null;
  versionNumber?: number | null;
  active?: boolean | null;
  uploadedAt?: string | null;
  revisionNumber?: number | null;
  replacedDocumentId?: number | null;
}

interface CommentItem {
  id: number;
  comment?: string | null;
  nextApproverId?: number | null;
  nextApproverName?: string | null;
  status?: string | null;
  originalFileName?: string | null;
  filePath?: string | null;
  createdByUserId?: number | null;
  createdByUserName?: string | null;
  createdAt?: string | null;
  attachmentType?: string | null;
}

interface Application {
  reviewAllowed?: boolean;
  applicantInstitutionalRole?: string;
  reviewOutcome?: string;
  flags?: InstitutionalFlag[];
  id: number;
  researchTitle?: string | null;
  researchDescription?: string | null;
  proposalDefended?: boolean | null;
  fieldwork?: boolean | null;
  relevantExperience?: string | null;
  coResearchersDetails?: string | null;
  academicQualifications?: string | null;
  fundingOfResearch?: string | null;
  researchDesign?: unknown;
  researchTool?: unknown;
  stageOneSubmittedAt?: string | null;
  categoryOfParticipants?: string | null;
  levelOfSensitivity?: string | null;
  researchLocation?: string | null;
  proposedFieldwork?: string | null;
  fieldworkDetails?: string | null;
  stageFourSubmittedAt?: string | null;
  declarationAccepted?: boolean | null;
  status: string;
  applicantId?: number | null;
  applicantName: string;
  applicantSurname: string;
  applicantFacultyCode?: string | null;
  applicantDepartmentCode?: string | null;
  applicantEcNumber?: string | null;
  applicantRegistrationNo?: string | null;
  applicantIdentifier?: string | null;
  applicantCategory?: string | null;
  applicantStudent?: boolean;
  facultyId?: number;
  facultyCode?: string | null;
  departmentCode?: string | null;
  currentWorkflowStageId?: number | null;
  currentWorkflowStageCode?: string | null;
  currentWorkflowAssignmentId?: number | null;
  currentStageEnteredAt?: string | null;
  currentStageDueAt?: string | null;
  currentStageCompletedAt?: string | null;
  currentStageTimeStatus?: string | null;
  revisionNumber?: number | null;
  returnedForCorrectionAt?: string | null;
  resubmittedAt?: string | null;
  documents: DocumentItem[];
  comments: CommentItem[];
  ethicalClearanceOriginalName?: string | null;
  ethicalClearancePath?: string | null;
  approvalLetterOriginalName?: string | null;
  approvalLetterPath?: string | null;
}

interface WorkflowActionResponse {
  applicationId: number;
  previousStatus?: string | null;
  newStatus?: string | null;
  completedStageCode?: string | null;
  nextStageCode?: string | null;
  nextResponsibleRole?: string | null;
  nextResponsibleUserId?: number | null;
  nextResponsibleUserName?: string | null;
  stageStartDate?: string | null;
  dueDate?: string | null;
  alternateUsed?: boolean;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
  errorCode?: string;
  traceId?: string;
  fieldErrors?: Record<string, string>;
}

interface User {
  id: number;
  name: string;
  surname: string;
  userType: string;
  facultyId?: number;
  facultyCode?: string;
  departmentCode?: string;
}

interface Faculty {
  id: number;
  code: string;
  name: string;
  description: string;
}

interface Department {
  id: number;
  departmentCode: string;
  name: string;
  description: string;
  facultyId: number;
}

const API_BASE = '';
const FILE_BASE = 'https://ethics.ri.msu.ac.zw';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function getFileNameFromPath(filePath: string | null | undefined): string {
  if (!filePath) return '';
  return filePath.split(/[/\\]/).pop() || '';
}

function trimLeadingSlashes(path: string): string {
  return path.replace(/^\/+/, '');
}

function isGeneratedApprovalPath(path: string | null | undefined): boolean {
  if (!path) return false;
  const normalized = trimLeadingSlashes(path).toLowerCase();
  return normalized.startsWith('upload/approvaldocuments/');
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function buildNormalDocumentCandidates(
  filePath: string | null | undefined,
  applicationId?: number | string
): string[] {
  const fileName = getFileNameFromPath(filePath);
  if (!fileName) return [];

  const encodedName = encodeURIComponent(fileName);
  const urls: string[] = [];

  if (applicationId !== undefined && applicationId !== null) {
    urls.push(`${FILE_BASE}/uploads/${encodeURIComponent(String(applicationId))}/${encodedName}`);
    urls.push(`/uploads/${encodeURIComponent(String(applicationId))}/${encodedName}`);
  }

  urls.push(`${FILE_BASE}/uploads/${encodedName}`);
  urls.push(`/uploads/${encodedName}`);

  return uniqueStrings(urls);
}

function buildGeneratedDocumentCandidates(filePath: string | null | undefined): string[] {
  if (!filePath) return [];

  const normalized = trimLeadingSlashes(filePath);
  const fileName = getFileNameFromPath(filePath);
  const encodedName = encodeURIComponent(fileName);

  return uniqueStrings([
    `${FILE_BASE}/${normalized}`,
    `/${normalized}`,
    `${FILE_BASE}/upload/approvaldocuments/${encodedName}`,
    `/upload/approvaldocuments/${encodedName}`,
  ]);
}

function buildFileCandidates(
  filePath: string | null | undefined,
  applicationId?: number | string
): string[] {
  if (!filePath) return [];
  if (isGeneratedApprovalPath(filePath)) {
    return buildGeneratedDocumentCandidates(filePath);
  }
  return buildNormalDocumentCandidates(filePath, applicationId);
}

async function fetchFileBlob(url: string): Promise<Blob> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch document (${res.status})`);
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('text/html')) {
    throw new Error('File URL returned HTML (wrong route)');
  }

  return res.blob();
}

async function openWithCandidates(
  urls: string[],
  fileName?: string,
  mode: 'open' | 'download' = 'open'
) {
  if (!urls.length) throw new Error('Document path is missing or invalid.');

  let lastError: unknown;

  for (const url of urls) {
    try {
      const blob = await fetchFileBlob(url);
      const blobUrl = window.URL.createObjectURL(blob);

      if (mode === 'open') {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      return;
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Unable to open document.');
}

const visibleStatuses: Record<string, string[]> = {
  APPLICANT: ['SUBMITTED'],
  DEPARTMENT: ['SUBMITTED'],
  FACULTY_RAP: ['RECOMMENDED'],
  FACULTY_ADMIN: ['RECOMMENDED'],
  INSTITUTE_REPRESENTATIVE: ['SUBMITTED', 'RECOMMENDED'],
  RESEARCH_OFFICER: [
    'SUBMITTED',
    'RECOMMENDED',
    'SEND_FOR_REVIEW',
    'REVIEWED',
    'SEND_TO_FINAL',
    'ACCEPTED',
  ],
  RESEARCH_CHAIR: ['SEND_FOR_REVIEW'],
  PROFESSOR: ['REVIEWED', 'SEND_TO_FINAL'],
  ADMIN: ['REVIEWED'],
};

const statusBadgeLabel = (status: string | null | undefined): string => {
  const s = (status || '').toUpperCase();
  switch (s) {
    case 'DRAFT':
      return 'Draft';
    case 'SUBMITTED':
      return 'Submitted';
    case 'RECOMMENDED':
      return 'Dept. Recommendation';
    case 'SEND_FOR_REVIEW':
      return 'Faculty Recommendation';
    case 'REVIEWED':
      return 'Research Chair Recommendation';
    case 'SEND_TO_FINAL':
      return 'Executive Director Confirmation';
    case 'ACCEPTED':
      return 'Accepted';
    case 'DECLINED':
      return 'Declined';
    case 'NOT_RECOMMENDED':
      return 'Revisions Required';
    case 'REJECTED':
      return 'Not Recommended';
    default:
      return s || 'N/A';
  }
};

const statusBadgeClass = (status: string | null | undefined): string => {
  const s = (status || '').toUpperCase();
  if (s === 'ACCEPTED') return 'apg-badge apg-badge-accepted';
  if (s === 'DECLINED') return 'apg-badge apg-badge-declined';
  if (s === 'NOT_RECOMMENDED') return 'apg-badge apg-badge-declined';
  if (s === 'DRAFT') return 'apg-badge apg-badge-draft';
  if (s === 'SUBMITTED') return 'apg-badge apg-badge-submitted';
  if (s === 'RECOMMENDED') return 'apg-badge apg-badge-recommended';
  if (s === 'SEND_FOR_REVIEW') return 'apg-badge apg-badge-faculty';
  if (s === 'REVIEWED') return 'apg-badge apg-badge-chair';
  if (s === 'SEND_TO_FINAL') return 'apg-badge apg-badge-directorate';
  if (s === 'REJECTED') return 'apg-badge apg-badge-declined';
  return 'apg-badge';
};

const progressStages: Record<string, number> = {
  SUBMITTED: 20,
  RECOMMENDED: 40,
  SEND_FOR_REVIEW: 60,
  REVIEWED: 80,
  SEND_TO_FINAL: 90,
  ACCEPTED: 100,
};

const computeProgress = (status: string | null | undefined): number =>
  progressStages[(status || '').toUpperCase()] || 0;

const workflowRoleLabel = (role?: string | null): string => {
  const key = (role || '').toUpperCase();
  const labels: Record<string, string> = {
    DEPARTMENT: 'Department Recommender',
    FACULTY_RAP: 'Faculty Recommender',
    FACULTY_ADMIN: 'Faculty Recommender',
    INSTITUTE_REPRESENTATIVE: 'Institute Representative',
    RESEARCH_CHAIR: 'Research Chair',
    PROFESSOR: 'Professor Approver',
    REVIEWER: 'Reviewer',
    ADMIN: 'Final Approver',
    ADMIN_CYBER: 'System Admin',
    RESEARCH_OFFICER: 'Research Officer',
  };
  return labels[key] || toTitleCase(key || 'Responsible Officer');
};

const deadlineStatusLabel = (status?: string | null, dueAt?: string | null, completedAt?: string | null): string => {
  if (completedAt) return 'Completed';
  const normalized = (status || '').toUpperCase();
  if (normalized === 'DUE_SOON') return 'Due Soon';
  if (normalized === 'OVERDUE') return 'Overdue';
  if (dueAt) {
    const due = new Date(dueAt);
    if (!Number.isNaN(due.getTime())) {
      const today = new Date();
      if (due.toDateString() === today.toDateString()) return 'Due Today';
      if (due.getTime() < today.getTime()) return 'Overdue';
    }
  }
  return 'On Track';
};

const deadlineBadgeClass = (label: string): string =>
  `apg-deadline-badge apg-deadline-${label.toLowerCase().replace(/\s+/g, '-')}`;

const LinearProgress: React.FC<{ value: number; label?: boolean }> = ({ value, label }) => {
  const pct = Math.max(0, Math.min(100, value));

  return (
    <div className="apg-progress">
      <div className="apg-progress-track">
        <div className="apg-progress-bar" style={{ width: `${pct}%` }} />
      </div>
      {label && <span className="apg-progress-label">{pct}%</span>}
    </div>
  );
};

function parseJsonObject(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      return {};
    }
  }

  return {};
}

function toTitleCase(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function getSelectedFlags(value: unknown): string[] {
  const parsed = parseJsonObject(value);
  return Object.entries(parsed)
    .filter(([, v]) => v === true)
    .map(([k]) => toTitleCase(k));
}

function safeText(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const str = String(value).trim();
  return str ? str : '—';
}


function buildSubmissionErrorMessage(err: unknown): string {
  if (axios.isAxiosError<ApiErrorResponse>(err)) {
    const data = err.response?.data;
    const fieldMessage = data?.fieldErrors
      ? Object.values(data.fieldErrors).filter(Boolean).join(' ')
      : '';
    const message = fieldMessage || data?.message || data?.error;
    const reference = data?.traceId ? ` Reference: ${data.traceId}.` : '';
    if (message) return `${message}${reference}`;
    if (err.response?.status === 403) return `You are not assigned to this workflow stage.${reference}`;
    if (err.response?.status === 409) return `This application has already moved to another workflow stage.${reference}`;
  }

  if (err instanceof Error && err.message) {
    return err.message;
  }

  return 'The request could not be completed.';
}

function humanizeBool(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function formatDocumentType(type?: string | null): string {
  if (!type) return '—';
  return toTitleCase(type);
}

function formatDocumentDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatFileSize(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const mib = bytes / 1024 / 1024;
  if (mib >= 1) return `${mib.toFixed(mib >= 10 ? 1 : 2)} MiB`;
  return `${(bytes / 1024).toFixed(1)} KiB`;
}

function documentKey(doc: DocumentItem): string {
  return `${doc.documentSource || 'DOCUMENT'}-${doc.id}-${doc.logicalDocumentKey || doc.documentType}`;
}

function buildDocumentUrls(doc: DocumentItem, applicationId: number, userId?: number | string): string[] {
  const urls: string[] = [];
  if (doc.downloadUrl && userId !== undefined && userId !== null) {
    const separator = doc.downloadUrl.includes('?') ? '&' : '?';
    urls.push(`${doc.downloadUrl}${separator}userId=${encodeURIComponent(String(userId))}`);
  }
  urls.push(...buildFileCandidates(doc.filePath, applicationId));
  return uniqueStrings(urls);
}

function buildWorkflowAttachmentUrls(
  comment: CommentItem,
  applicationId: number,
  userId?: number | string
): string[] {
  const urls: string[] = [];
  if (userId !== undefined && userId !== null) {
    urls.push(
      `${API_BASE}/v1/api/documents/applications/${encodeURIComponent(String(applicationId))}` +
      `/workflow-attachments/${encodeURIComponent(String(comment.id))}/download?userId=${encodeURIComponent(String(userId))}`
    );
  }
  urls.push(...buildFileCandidates(comment.filePath || comment.originalFileName, applicationId));
  return uniqueStrings(urls);
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="apg-detail-row">
    <div className="apg-detail-label">{label}</div>
    <div className="apg-detail-value">{value ?? '—'}</div>
  </div>
);

const DocumentList: React.FC<{
  documents: DocumentItem[];
  applicationId: number;
  userId?: number | string;
  readStates: boolean[];
  onToggleRead: (index: number) => void;
}> = ({ documents, applicationId, userId, readStates, onToggleRead }) => {
  const [query, setQuery] = useState('');
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const filteredDocuments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => [
      doc.documentType,
      doc.originalFileName,
      doc.uploadedByUserName,
      doc.workflowStageName,
      doc.applicationSection,
      doc.documentSource,
    ].some((value) => (value || '').toLowerCase().includes(q)));
  }, [documents, query]);

  if (!documents.length) {
    return <p className="apg-muted">No documents found.</p>;
  }

  return (
    <div className="apg-document-list">
      <div className="apg-document-toolbar">
        <input
          type="search"
          className="apg-document-search"
          placeholder="Search documents"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search documents"
        />
      </div>
      {downloadError ? <div className="apg-inline-error">{downloadError}</div> : null}
      {filteredDocuments.length ? (
        <div className="apg-table-scroll" tabIndex={0} aria-label="Documents table">
          <table className="app-modal-docs-table apg-documents-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Version</th>
                <th>Read?</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => {
                const originalIndex = documents.findIndex((item) => documentKey(item) === documentKey(doc));
                const disabled = originalIndex > 0 && !readStates[originalIndex - 1];
                const label =
                  doc.originalFileName ||
                  getFileNameFromPath(doc.filePath) ||
                  doc.documentType ||
                  'Document';
                const urls = buildDocumentUrls(doc, applicationId, userId);
                const key = documentKey(doc);
                const uploadedAt = (doc as DocumentItem & { uploadedAt?: string | null }).uploadedAt;

                return (
                  <tr key={key} className={disabled ? 'faded' : ''}>
                    <td><strong>{label}</strong><small className="apg-document-kind">{formatDocumentType(doc.documentType)}</small>
                      <details className="apg-document-metadata"><summary>Details</summary>
                        <div>Size: {formatFileSize(doc.fileSize)}</div>
                        <div>Uploaded: {formatDocumentDateTime(uploadedAt)}</div>
                        <div>By: {safeText(doc.uploadedByUserName)}</div>
                        <div>Stage: {safeText(doc.workflowStageName || doc.applicationSection || doc.documentSource)}</div>
                      </details>
                    </td>
                    <td>
                      <span className="apg-version-badge">v{doc.versionNumber ?? 1}</span>
                      {doc.active === false ? (
                        <span className="apg-document-status">Previous</span>
                      ) : (
                        <span className="apg-document-status apg-document-status-current">Current</span>
                      )}
                    </td>
                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={!!readStates[originalIndex]}
                          onChange={() => onToggleRead(originalIndex)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="app-download-btn"
                        disabled={!readStates[originalIndex] || !urls.length || openingKey === key}
                        onClick={async () => {
                          setOpeningKey(key);
                          setDownloadError(null);
                          try {
                            await openWithCandidates(urls, label, 'open');
                          } catch (e) {
                            setDownloadError(e instanceof Error ? e.message : 'Unable to open document.');
                          } finally {
                            setOpeningKey(null);
                          }
                        }}
                      >
                        {openingKey === key ? 'Opening...' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="apg-muted">No documents match your search.</p>
      )}
    </div>
  );
};

const ApprovalInbox: React.FC = () => {
  const { account, token } = useAuth();
  const user = account as User | null;

  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [isReviewMode, setIsReviewMode] = useState(false);

  const [comment, setComment] = useState('');
  const [revisionOutcome, setRevisionOutcome] = useState('REVISION_REQUIRED');
  const [files, setFiles] = useState<File[]>([]);
  const [readStates, setReadStates] = useState<boolean[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingGeneratedDoc, setUploadingGeneratedDoc] = useState(false);

  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [facultyFilter, setFacultyFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [isNamesLoading, setIsNamesLoading] = useState(false);

  const sigPadRef = useRef<InstanceType<typeof SignatureCanvas> | null>(null);

  const [ethicalClearancePDF, setEthicalClearancePDF] = useState<Blob | null>(null);
  const [ethicalClearanceUrl, setEthicalClearanceUrl] = useState<string | null>(null);

  const [approvalPDF, setApprovalPDF] = useState<Blob | null>(null);
  const [approvalPDFUrl, setApprovalPDFUrl] = useState<string | null>(null);

  const [clearanceNumber, setClearanceNumber] = useState('');

  const inboxStatuses = user ? visibleStatuses[user.userType] || [] : [];

  const revokeBlobUrls = () => {
    if (ethicalClearanceUrl) URL.revokeObjectURL(ethicalClearanceUrl);
    if (approvalPDFUrl) URL.revokeObjectURL(approvalPDFUrl);
  };

  const resetGeneratedDocs = () => {
    revokeBlobUrls();
    setEthicalClearancePDF(null);
    setEthicalClearanceUrl(null);
    setApprovalPDF(null);
    setApprovalPDFUrl(null);
    setClearanceNumber('');
  };

  const uploadGeneratedDocument = async (
    applicationId: number,
    file: Blob,
    type: 'ethicalClearanceFile' | 'approvalLetterFile',
    fileName: string
  ) => {
    const formData = new FormData();
    formData.append(type, file, fileName);
    const query = user?.id !== undefined && user?.id !== null ? `?userId=${encodeURIComponent(String(user.id))}` : '';

    const res = await axios.post(
      `${API_BASE}/v1/api/approval/upload-documents/${applicationId}${query}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!res.data?.success || !res.data?.data?.[type === 'ethicalClearanceFile' ? 'ethicalClearancePath' : 'approvalLetterPath']) {
      throw new Error(res.data?.message || 'The generated document was not saved. Please try again.');
    }
    return res.data;
  };

  useEffect(() => {
    const fetchFaculties = async () => {
      try {
        const res = await axios.get(`${API_BASE}/v1/api/systems-settings/faculties/get-all`);
        setFaculties(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (err) {
        console.error('Failed to load faculties', err);
      }
    };

    const fetchDepartments = async () => {
      try {
        const res = await axios.get(`${API_BASE}/v1/api/departments/all`);
        setDepartments(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (err) {
        console.error('Failed to load departments', err);
      }
    };

    fetchFaculties();
    fetchDepartments();
  }, []);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!user) return;

      const url =
        user.userType === 'DEPARTMENT'
          ? `${API_BASE}/v1/api/approval/departments/inbox/${user.id}`
          : `${API_BASE}/v1/api/approval/approval-inbox/${user.id}`;

      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.success) {
          const incoming = (res.data.data || []) as Application[];
          setApplications(
            incoming.filter((app: Application) => app.reviewAllowed || inboxStatuses.includes(app.status))
          );
        }
      } catch (err) {
        console.error('Failed to load applications', err);
      }
    };

    fetchApplications();
  }, [user, token, inboxStatuses.join('|')]);

  useEffect(() => {
    return () => {
      revokeBlobUrls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDetails = async (appId: number, review: boolean) => {
    try {
      setErrorMessage(null);
      const detailsUrl =
        user?.id !== undefined && user?.id !== null
          ? `${API_BASE}/v1/api/approval/inbox/${encodeURIComponent(String(user.id))}/applications/${appId}`
          : `${API_BASE}/v1/api/research-applications/details/${appId}`;
      const res = await axios.get(detailsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        const appData = res.data.data as Application;

        resetGeneratedDocs();
        setSelectedApp(appData);
        setIsReviewMode(review);
        setComment('');
        setFiles([]);
        setReadStates(new Array(appData.documents?.length || 0).fill(false));
        sigPadRef.current?.clear();

        const comments = appData.comments || [];
        const missingIds = Array.from(
          new Set(
            comments
              .filter(
                (c) =>
                  !c.createdByUserName &&
                  c.createdByUserId &&
                  !userNames[c.createdByUserId]
              )
              .map((c) => c.createdByUserId as number)
          )
        );

        if (missingIds.length > 0) {
          setIsNamesLoading(true);

          const results = await Promise.all(
            missingIds.map(async (id) => {
              try {
                const r = await axios.get(`${API_BASE}/v1/api/users/details/${id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });

                if (r.data?.success && r.data?.data) {
                  const { title, name, surname } = r.data.data;
                  return {
                    id,
                    name: `${title ? `${title} ` : ''}${name} ${surname}`.trim(),
                  };
                }
              } catch (err) {
                console.error(`Failed to load user ${id}`, err);
              }

              return null;
            })
          );

          const add: Record<number, string> = {};
          results.forEach((row) => {
            if (row) add[row.id] = row.name;
          });

          setUserNames((prev) => ({ ...prev, ...add }));
          setIsNamesLoading(false);
        }
      }
    } catch (err) {
      console.error('Error loading details:', err);
      setErrorMessage(
        axios.isAxiosError(err)
          ? err.response?.data?.message || err.response?.data?.error || 'Unable to open this application for review.'
          : 'Unable to open this application for review.'
      );
    }
  };

  useEffect(() => {
    const id = Number(new URLSearchParams(window.location.search).get('applicationId'));
    if (id > 0 && user?.id && token) void loadDetails(id, true);
    // Open the requested application once the authenticated account is available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  const handleReviewClick = (app: Application) => loadDetails(app.id, true);
  const handleViewClick = (app: Application) => loadDetails(app.id, false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const selectedFiles = Array.from(e.target.files);
      const oversized = selectedFiles.find((file) => file.size > MAX_ATTACHMENT_BYTES);
      if (oversized) {
        setFiles([]);
        e.target.value = '';
        setErrorMessage(`The selected attachment is invalid. ${oversized.name} exceeds 10 MiB.`);
        return;
      }
      setFiles(selectedFiles);
    }
  };



  const getFacultyName = () => {
    if (!selectedApp) return '';
    const code = selectedApp.facultyCode || selectedApp.applicantFacultyCode || '';
    const fac = faculties.find((f) => f.code === code);
    return fac ? fac.name : code;
  };

  const getApplicantCategoryLabel = () => {
    if (!selectedApp) return 'staff member';
    const identifier =
      selectedApp.applicantIdentifier ||
      selectedApp.applicantRegistrationNo ||
      selectedApp.applicantEcNumber ||
      '';
    const isStudent =
      selectedApp.applicantStudent ||
      selectedApp.applicantCategory === 'STUDENT' ||
      identifier.trim().toLowerCase().startsWith('r');
    return isStudent ? 'student' : 'staff member';
  };

  const generateClearancePDF = async () => {
    if (!selectedApp || !user) return;
    if(user.userType !== 'RESEARCH_CHAIR' || !sigPadRef.current || sigPadRef.current.isEmpty()) {
      setErrorMessage('The REC Chairperson must sign in the signature box before generating the ethical clearance letter.');
      return;
    }

    try {
      setUploadingGeneratedDoc(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      if (ethicalClearanceUrl) URL.revokeObjectURL(ethicalClearanceUrl);
      setEthicalClearancePDF(null);
      setEthicalClearanceUrl(null);

      const dateStr = new Date().toLocaleDateString();
      const record = await toolsApi('/clearances/applications/'+selectedApp.id+'/prepare','POST');
      const clearanceNo = record.clearanceNumber;
      const qrResponse = await fetch(API_BASE+'/v1/api/clearances/applications/'+selectedApp.id+'/qr');
      if(!qrResponse.ok)throw new Error('Unable to generate verification QR');
      const qrData = await new Promise<string>((resolve,reject)=>{qrResponse.blob().then(blob=>{const reader=new FileReader();reader.onload=()=>resolve(String(reader.result));reader.onerror=reject;reader.readAsDataURL(blob);}).catch(reject);});
      setClearanceNumber(clearanceNo);

      const blob = await generateLetterPdf({
        kind: 'clearance', date: dateStr,
        applicantName: selectedApp.applicantName, applicantSurname: selectedApp.applicantSurname,
        researchTitle: selectedApp.researchTitle || '', faculty: getFacultyName(),
        applicantCategory: getApplicantCategoryLabel(), clearanceNumber: clearanceNo,
        verificationUrl: record.verificationUrl, qrData,
        signatureDataUrl: sigPadRef.current?.toDataURL?.('image/png') || '',
        signatoryName: user.name + ' ' + user.surname,
      });
      await uploadGeneratedDocument(
        selectedApp.id,
        blob,
        'ethicalClearanceFile',
        'Ethical_Clearance.pdf'
      );

      setEthicalClearancePDF(blob);
      setEthicalClearanceUrl(URL.createObjectURL(blob));

      setSuccessMessage('Ethical Clearance PDF generated and uploaded successfully.');
    } catch (err) {
      console.error('Error generating/uploading ethical clearance PDF:', err);
      setErrorMessage(buildSubmissionErrorMessage(err));
    } finally {
      setUploadingGeneratedDoc(false);
    }
  };

  const generateAdminApprovalPDF = async () => {
    if (!selectedApp || !user) return;
    if(!['ADMIN', 'PROFESSOR'].includes(user.userType) || !sigPadRef.current || sigPadRef.current.isEmpty()) {
      setErrorMessage('The Executive Director must sign in the signature box before generating the confirmation of approval.');
      return;
    }

    try {
      setUploadingGeneratedDoc(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      if (approvalPDFUrl) URL.revokeObjectURL(approvalPDFUrl);
      setApprovalPDF(null);
      setApprovalPDFUrl(null);

      const dateStr = new Date().toLocaleDateString();

      const record = await toolsApi('/clearances/applications/'+selectedApp.id+'/prepare','POST');
      let clearanceNo = record.clearanceNumber;
      const qrResponse = await fetch(API_BASE+'/v1/api/clearances/applications/'+selectedApp.id+'/qr');
      if(!qrResponse.ok)throw new Error('Unable to generate verification QR');
      const qrData = await new Promise<string>((resolve,reject)=>{qrResponse.blob().then(blob=>{const reader=new FileReader();reader.onload=()=>resolve(String(reader.result));reader.onerror=reject;reader.readAsDataURL(blob);}).catch(reject);});
      if (!clearanceNo) {
        for (let i = selectedApp.comments.length - 1; i >= 0; i -= 1) {
          const c = selectedApp.comments[i];
          if (c.comment && c.comment.includes('Ethical Clearance No:')) {
            const match = c.comment.match(/Ethical Clearance No:\s*([A-Z]+\d+)/);
            if (match) {
              clearanceNo = match[1];
              break;
            }
          }
        }
      }

      const blob = await generateLetterPdf({
        kind: 'approval', date: dateStr,
        applicantName: selectedApp.applicantName, applicantSurname: selectedApp.applicantSurname,
        researchTitle: selectedApp.researchTitle || '', faculty: getFacultyName(),
        applicantCategory: getApplicantCategoryLabel(), clearanceNumber: clearanceNo,
        verificationUrl: record.verificationUrl, qrData,
        signatureDataUrl: sigPadRef.current?.toDataURL?.('image/png') || '',
        signatoryName: user.name + ' ' + user.surname,
      });
      await uploadGeneratedDocument(
        selectedApp.id,
        blob,
        'approvalLetterFile',
        'Approval_Letter.pdf'
      );

      setApprovalPDF(blob);
      setApprovalPDFUrl(URL.createObjectURL(blob));

      setSuccessMessage('Approval Letter PDF generated and uploaded successfully.');
    } catch (err) {
      console.error('Error generating/uploading approval PDF:', err);
      setErrorMessage(buildSubmissionErrorMessage(err));
    } finally {
      setUploadingGeneratedDoc(false);
    }
  };

  const determineNextStatus = (defer?: boolean): string => {
    if (defer) return revisionOutcome;
    if (!user || !selectedApp) return '';

    switch (user.userType) {
      case 'DEPARTMENT':
        return 'RECOMMENDED';
      case 'INSTITUTE_REPRESENTATIVE':
        return 'RECOMMENDED';
      case 'FACULTY_RAP':
      case 'FACULTY_ADMIN':
        return 'SEND_FOR_REVIEW';
      case 'RESEARCH_CHAIR':
        return 'REVIEWED';
      case 'PROFESSOR':
      case 'ADMIN':
        return 'ACCEPTED';
      default:
        return selectedApp.currentWorkflowAssignmentId ? 'RECOMMENDED' : '';
    }
  };

  const actionLabel = useMemo(() => {
    const role = user?.userType;
    if (role === 'ADMIN' || role === 'PROFESSOR') return 'Confirmation of Approval';
    if (role === 'RESEARCH_CHAIR') return 'Recommend';
    if (role === 'DEPARTMENT' || role === 'FACULTY_RAP' || role === 'FACULTY_ADMIN' || role === 'INSTITUTE_REPRESENTATIVE') {
      return 'Recommend';
    }
    return 'Submit';
  }, [user?.userType]);

  const handleSubmit = async (defer = false) => {
    if (submitting || uploadingGeneratedDoc) return;
    if (!selectedApp || !user) return;

    if (!defer && ((user.userType === 'RESEARCH_CHAIR' && !ethicalClearancePDF)
        || (['ADMIN', 'PROFESSOR'].includes(user.userType) && !approvalPDF))) {
      setErrorMessage('Generate and save your signed letter before submitting this decision.');
      return;
    }

    const newStatus = determineNextStatus(defer);
    if (!newStatus) return;
    if (defer && !comment.trim()) {
      setErrorMessage('Revision remarks are required when an application is marked Not Recommended.');
      return;
    }

    const selectedAttachment = files.find((file) => file.size > MAX_ATTACHMENT_BYTES);
    if (selectedAttachment) {
      setErrorMessage(`The selected attachment is invalid. ${selectedAttachment.name} exceeds 10 MiB.`);
      return;
    }

    const form = new FormData();
    form.append('applicationId', String(selectedApp.id));
    form.append(
      'comment',
      `${comment}${clearanceNumber ? `\nEthical Clearance No: ${clearanceNumber}` : ''}`
    );
    form.append('newStatus', newStatus);
    form.append('createdByUserId', String(user.id));

    if (['ADMIN', 'PROFESSOR'].includes(user.userType) && approvalPDF) {
      form.append('attachments', approvalPDF, 'Approval_Letter.pdf');
    } else if (user.userType === 'RESEARCH_CHAIR' && ethicalClearancePDF) {
      form.append('attachments', ethicalClearancePDF, 'Ethical_Clearance.pdf');
    } else if (files.length) {
      files.forEach((file) => form.append('attachments', file, file.name));
    }

    try {
      setSubmitting(true);

      const res = await axios.post(`${API_BASE}/v1/api/approval/submit`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = res.data.data as WorkflowActionResponse;

      const nextText = result?.nextResponsibleUserName
        ? ` Next: ${result.nextResponsibleUserName}.`
        : '';
      setSuccessMessage(defer ? 'Not recommended successfully.' : `Submission successful.${nextText}`);

      setApplications((prev) => prev.filter((a) => a.id !== selectedApp.id));
      setSelectedApp(null);
      sigPadRef.current?.clear();
      resetGeneratedDocs();
      setComment('');
      setFiles([]);
    } catch (err) {
      console.error('Workflow submission failed', err);
      setErrorMessage(buildSubmissionErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const uniqueFacultyCodes = Array.from(
    new Set(applications.map((a) => a.facultyCode).filter(Boolean))
  ) as string[];

  const uniqueDepartmentCodes = Array.from(
    new Set(applications.map((a) => a.departmentCode).filter(Boolean))
  ) as string[];

  const filteredApps = applications.filter((app) => {
    let ok = true;

    if (dateFilter) ok = (app.stageOneSubmittedAt || '').slice(0, 10) === dateFilter;
    if (ok && facultyFilter) ok = (app.facultyCode || '') === facultyFilter;
    if (ok && departmentFilter) ok = (app.departmentCode || '') === departmentFilter;

    if (ok && search.trim()) {
      const q = search.trim().toLowerCase();
      ok =
        (app.researchTitle || '').toLowerCase().includes(q) ||
        `${app.applicantName} ${app.applicantSurname}`.toLowerCase().includes(q);
    }

    return ok;
  });

  const totalPages = Math.ceil(filteredApps.length / perPage) || 1;
  const paginatedApps = filteredApps.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [filteredApps.length, totalPages, page]);

  const formatDate = (dateStr?: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : 'N/A';

  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString();
  };

  const toggleRead = (i: number) => {
    setReadStates((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  const fileUploadLabel = (): string => {
    if (!user) return 'Upload File:';

    switch (user.userType) {
      case 'ADMIN':
      case 'PROFESSOR':
      case 'RESEARCH_CHAIR':
        return 'Signature:';
      case 'FACULTY_RAP':
      case 'FACULTY_ADMIN':
        return 'Upload Faculty Meeting Minutes:';
      case 'INSTITUTE_REPRESENTATIVE':
        return 'Upload Institute Recommendation File:';
      case 'DEPARTMENT':
        return 'Upload Recommendation File:';
      default:
        return 'Upload File:';
    }
  };

  const commentAttachmentNiceLabel = (c: CommentItem): string => {
    switch ((c.status || '').toUpperCase()) {
      case 'RECOMMENDED':
        return 'Department/Faculty Recommendation';
      case 'SEND_FOR_REVIEW':
        return 'Faculty Meeting Minutes';
      case 'REVIEWED':
        return 'Ethical Clearance / Chair Signature';
      case 'SEND_TO_FINAL':
        return 'To Directorate';
      case 'ACCEPTED':
        return 'Admin Approval';
      case 'REJECTED':
        return 'Not Recommended';
      case 'NOT_RECOMMENDED':
        return 'Not Recommended';
      default:
        return 'Attachment';
    }
  };

  const selectedResearchDesign = useMemo(
    () => getSelectedFlags(selectedApp?.researchDesign),
    [selectedApp]
  );

  const selectedResearchTools = useMemo(
    () => getSelectedFlags(selectedApp?.researchTool),
    [selectedApp]
  );

  return (
    <main className="app-application-page">
      <section className="app-headerCard">
        <h1 className="app-heading" tabIndex={0}>
          Approval Inbox
        </h1>
      </section>

      <StatCards items={[
        {label:'Role inbox',value:applications.length,total:applications.length,meterLabel:'Accessible inbox'},
        {label:'Matching filters',value:filteredApps.length,total:applications.length,meterLabel:'Current search results'},
        {label:'Applicant is you',value:applications.filter(a=>a.applicantId===user?.id).length,total:applications.length,meterLabel:'Of inbox applications',tone:'warning',note:'Applicant and role responsibilities stay separate'}
      ]}/>
      <section className="app-filter-bar">
        <div className="app-search-bar">
          <input
            className="app-search-input"
            type="text"
            placeholder="Search title or applicant"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            aria-label="Search by research title or applicant"
          />
        </div>

        <label>
          Faculty:{' '}
          <select
            className="approval-select"
            value={facultyFilter}
            onChange={(e) => {
              setFacultyFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {uniqueFacultyCodes.map((fc) => {
              const fac = faculties.find((f) => f.code === fc);
              return (
                <option key={fc} value={fc}>
                  {fac ? fac.name : fc}
                </option>
              );
            })}
          </select>
        </label>

        <label>
          Department:{' '}
          <select
            className="approval-select"
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {uniqueDepartmentCodes.map((dc) => {
              const dep = departments.find((d) => d.departmentCode === dc);
              return (
                <option key={dc} value={dc}>
                  {dep ? dep.name : dc}
                </option>
              );
            })}
          </select>
        </label>

        <label>
          Date:{' '}
          <input
            className="approval-date"
            type="date"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPage(1);
            }}
            max={new Date().toISOString().slice(0, 10)}
          />
        </label>

        <span className="approval-pagination-select">
          Show{' '}
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>{' '}
          per page
        </span>
      </section>

      <nav className="app-pagination-controls" aria-label="Pagination">
        <button
          className="approval-pagination-btn"
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
        >
          Prev
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          className="approval-pagination-btn"
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
        >
          Next
        </button>
        <span className="approval-results-count">
          {filteredApps.length} result{filteredApps.length === 1 ? '' : 's'}
        </span>
      </nav>

      <div className="app-table-container">
        <table className="app-table apg-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Title</th>
              <th className="hide-sm">Status</th>
              <th>Progress</th>
              <th className="hide-sm">Date Applied</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApps.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                  No Applications Yet
                </td>
              </tr>
            ) : (
              paginatedApps.map((app) => {
                const pct = computeProgress(app.status);

                return (
                  <tr key={app.id}>
                    <td className="apg-applicant-name">
                      {`${safeText(app.applicantName)} ${safeText(app.applicantSurname)}`.replace('â€” â€”', 'â€”')}
                    </td>
                    <td>{app.researchTitle}<ApplicationFlags flags={app.flags} /></td>
                    <td className="hide-sm">
                      <span className={statusBadgeClass(app.status)}>
                        {statusBadgeLabel(app.reviewOutcome || app.status)}
                      </span>
                    </td>
                    <td>
                      <LinearProgress value={pct} label />
                    </td>
                    <td className="hide-sm">{formatDate(app.stageOneSubmittedAt)}</td>
                    <td>
                      {app.reviewAllowed ? (
                        <button
                          className="app-view-btn"
                          onClick={() => handleReviewClick(app)}
                        >
                          {user?.userType === 'ADMIN' ? 'Review (Final)' : 'Review'}
                        </button>
                      ) : (
                        <button className="app-view-btn" onClick={() => handleViewClick(app)}>
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedApp && (
        <ModalLayer
          className="app-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="workflow-review-title"
          onClick={() => {
            resetGeneratedDocs();
            setSelectedApp(null);
          }}
        >
          <div
            className="app-modal-content apg-details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="app-modal-close"
              onClick={() => {
                resetGeneratedDocs();
                setSelectedApp(null);
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div className="apg-modal-head">
              <h2 id="workflow-review-title" className="app-modal-title">{safeText(selectedApp.researchTitle)}</h2>

              <div className="apg-status-inline">
                <strong>Status:</strong>{' '}
                <span className={statusBadgeClass(selectedApp.status)}>
                  {statusBadgeLabel(selectedApp.reviewOutcome || selectedApp.status)}
                </span>
              </div>
            </div>

            <div style={{ margin: '8px 0 16px' }}>
              <LinearProgress value={computeProgress(selectedApp.status)} label />
            </div>

            <div className="apg-details-sections">
              <section className="apg-detail-card">
                <h3>Applicant Details</h3>
                <p>Applicant role: {roleLabel(selectedApp.applicantInstitutionalRole)}</p>
                {isReviewMode && selectedApp.applicantId === user?.id && <p className="journey-notice">Applicant is also acting as {roleLabel(user?.userType)}. This will be recorded in the audit history.</p>}
                <div className="apg-detail-grid">
                  <DetailRow label="Applicant ID" value={safeText(selectedApp.applicantId)} />
                  <DetailRow
                    label="Full Name"
                    value={`${safeText(selectedApp.applicantName)} ${safeText(
                      selectedApp.applicantSurname
                    )}`.replace('— —', '—')}
                  />
                  <DetailRow
                    label="Faculty Code"
                    value={safeText(selectedApp.facultyCode || selectedApp.applicantFacultyCode)}
                  />
                  <DetailRow
                    label="Department Code"
                    value={safeText(selectedApp.departmentCode || selectedApp.applicantDepartmentCode)}
                  />
                </div>
              </section>

              <section className="apg-detail-card">
                <h3>Application Summary</h3>
                <div className="apg-detail-grid">
                  <DetailRow label="Application ID" value={selectedApp.id} />
                  <DetailRow
                    label="Date Applied"
                    value={formatDateTime(selectedApp.stageOneSubmittedAt)}
                  />
                  <DetailRow
                    label="Stage Four Submitted"
                    value={formatDateTime(selectedApp.stageFourSubmittedAt)}
                  />
                  <DetailRow
                    label="Declaration Accepted"
                    value={humanizeBool(selectedApp.declarationAccepted)}
                  />
                  <DetailRow
                    label="Proposal Defended"
                    value={humanizeBool(selectedApp.proposalDefended)}
                  />
                  <DetailRow
                    label="Fieldwork"
                    value={humanizeBool(selectedApp.fieldwork)}
                  />
                  <DetailRow
                    label="Category of Participants"
                    value={safeText(selectedApp.categoryOfParticipants)}
                  />
                  <DetailRow
                    label="Level of Sensitivity"
                    value={safeText(selectedApp.levelOfSensitivity)}
                  />
                  <DetailRow
                    label="Research Location"
                    value={safeText(selectedApp.researchLocation)}
                  />
                  <DetailRow
                    label="Funding of Research"
                    value={safeText(selectedApp.fundingOfResearch)}
                  />
                </div>
              </section>

              <section className="apg-detail-card">
                <h3>Research Details</h3>
                <div className="apg-detail-grid apg-detail-grid-single">
                  <DetailRow
                    label="Research Title"
                    value={safeText(selectedApp.researchTitle)}
                  />
                  <DetailRow
                    label="Research Description"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.researchDescription)}
                      </div>
                    }
                  />
                  <DetailRow
                    label="Relevant Experience"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.relevantExperience)}
                      </div>
                    }
                  />
                  <DetailRow
                    label="Co-Researchers Details"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.coResearchersDetails)}
                      </div>
                    }
                  />
                  <DetailRow
                    label="Academic Qualifications"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.academicQualifications)}
                      </div>
                    }
                  />
                  <DetailRow
                    label="Proposed Fieldwork"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.proposedFieldwork)}
                      </div>
                    }
                  />
                  <DetailRow
                    label="Fieldwork Details"
                    value={
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {safeText(selectedApp.fieldworkDetails)}
                      </div>
                    }
                  />
                </div>
              </section>

              <section className="apg-detail-card">
                <h3>Research Design</h3>
                {selectedResearchDesign.length ? (
                  <ul className="apg-pill-list">
                    {selectedResearchDesign.map((item) => (
                      <li key={item} className="apg-pill">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="apg-muted">No research design selected.</p>
                )}
              </section>

              <section className="apg-detail-card">
                <h3>Research Tools</h3>
                {selectedResearchTools.length ? (
                  <ul className="apg-pill-list">
                    {selectedResearchTools.map((item) => (
                      <li key={item} className="apg-pill">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="apg-muted">No research tools selected.</p>
                )}
              </section>

              <section className="apg-detail-card">
                <h3>Documents</h3>
                <DocumentList
                  documents={selectedApp.documents || []}
                  applicationId={selectedApp.id}
                  userId={user?.id}
                  readStates={readStates}
                  onToggleRead={toggleRead}
                />
              </section>



              {selectedApp.comments?.length ? (
                <section className="apg-detail-card">
                  <h3>Comments / Workflow Trail</h3>
                  <div className="apg-table-scroll" tabIndex={0} aria-label="Comments and workflow trail table">
                    <table className="app-modal-comments-table">
                      <thead>
                        <tr>
                          <th>Reviewer / Date</th>
                          <th>Comment</th>
                          <th>Attachment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isNamesLoading ? (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: 10 }}>
                              Loading...
                            </td>
                          </tr>
                        ) : (
                          selectedApp.comments.map((c) => {
                            const urls = buildWorkflowAttachmentUrls(c, selectedApp.id, user?.id);
                            const label =
                              c.originalFileName ||
                              getFileNameFromPath(c.filePath) ||
                              commentAttachmentNiceLabel(c);

                            return (
                              <tr key={c.id}>
                                <td>
                                  <div>
                                    {c.createdByUserName ||
                                      (c.createdByUserId
                                        ? userNames[c.createdByUserId] || ''
                                        : '') ||
                                      c.nextApproverName ||
                                      '—'}
                                  </div>
                                  {c.nextApproverName ? (
                                    <small className="apg-muted">
                                      Next Recommender: {c.nextApproverName}
                                    </small>
                                  ) : null}
                                  <small className="apg-document-kind">{formatDateTime(c.createdAt)}</small>
                                </td>
                                <td style={{ whiteSpace: 'pre-wrap' }}>{safeText(c.comment)}<br/>
                                  <span className={statusBadgeClass(c.status)}>
                                    {statusBadgeLabel(c.status)}
                                  </span>
                                </td>
                                <td>
                                  {urls.length ? (
                                    <button
                                      type="button"
                                      className="apg-link"
                                      onClick={async () => {
                                        try {
                                          await openWithCandidates(urls, label, 'open');
                                        } catch (e: unknown) {
                                          const errorMessage =
                                            e instanceof Error
                                              ? e.message
                                              : 'Unable to open attachment.';
                                          alert(errorMessage);
                                        }
                                      }}
                                    >
                                      {label}
                                    </button>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {isReviewMode &&
              selectedApp.reviewAllowed &&
              readStates.length > 0 &&
              readStates.every(Boolean) ? (
                <div className="review-section animated">
                  <div className="apg-auto-route-summary" aria-live="polite">
                    <strong>This application will be sent automatically by the workflow engine.</strong>
                    <div className="apg-detail-grid">
                      <DetailRow label="Current Stage" value={safeText(selectedApp.currentWorkflowStageCode)} />
                      <DetailRow label="Responsible Role" value={workflowRoleLabel(user?.userType)} />
                      <DetailRow
                        label="Due Date"
                        value={
                          selectedApp.currentStageDueAt ? (
                            <>
                              {formatDateTime(selectedApp.currentStageDueAt)}{' '}
                              <span className={deadlineBadgeClass(deadlineStatusLabel(selectedApp.currentStageTimeStatus, selectedApp.currentStageDueAt, selectedApp.currentStageCompletedAt))}>
                                {deadlineStatusLabel(selectedApp.currentStageTimeStatus, selectedApp.currentStageDueAt, selectedApp.currentStageCompletedAt)}
                              </span>
                            </>
                          ) : '—'
                        }
                      />
                      <DetailRow
                        label="Next Routing"
                        value="The backend will determine the next stage and assigned user after this action succeeds."
                      />
                    </div>
                  </div>

                  <label htmlFor="workflow-comment">
                    {determineNextStatus(true) === 'NOT_RECOMMENDED' ? 'Decision comments' : 'Comment'}
                  </label>
                  <textarea
                    id="workflow-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Enter comments for the workflow record"
                    aria-describedby="workflow-comment-help"
                  />
                  <small id="workflow-comment-help" className="apg-muted">
                    Revision remarks are required when selecting Not Recommended.
                  </small>

                  {user && (user.userType === 'RESEARCH_CHAIR' || ['ADMIN', 'PROFESSOR'].includes(user.userType)) ? (
                    <>
                      <label>{fileUploadLabel()}</label>
                      <p className="apg-muted">Sign as {user.name} {user.surname}. This signature is embedded in your {user.userType === 'RESEARCH_CHAIR' ? 'ethical clearance letter' : 'confirmation of approval'}.</p>
                      <SignatureCanvas
                        ref={sigPadRef}
                        canvasProps={{ className: 'signature-canvas', 'aria-label': 'Approver signature', role: 'img' }}
                      />

                      <div className="signature-actions">
                        <button
                          type="button"
                          className="app-view-btn"
                          onClick={() => sigPadRef.current?.clear()}
                        >
                          Clear
                        </button>

                        {user.userType === 'RESEARCH_CHAIR' && (
                          <button
                            type="button"
                            className="app-view-btn"
                            onClick={generateClearancePDF}
                            disabled={uploadingGeneratedDoc}
                          >
                            {uploadingGeneratedDoc
                              ? 'Generating...'
                              : 'Generate Ethical Clearance PDF'}
                          </button>
                        )}

                        {ethicalClearanceUrl && (
                          <a
                            href={ethicalClearanceUrl}
                            download="Ethical_Clearance.pdf"
                            className="app-view-btn success-link"
                          >
                            Download Clearance Letter
                          </a>
                        )}

                        {['ADMIN', 'PROFESSOR'].includes(user.userType) && (
                          <button
                            type="button"
                            className="app-view-btn"
                            onClick={generateAdminApprovalPDF}
                            disabled={uploadingGeneratedDoc}
                          >
                            {uploadingGeneratedDoc
                              ? 'Generating...'
                              : 'Generate Approval PDF'}
                          </button>
                        )}

                        {approvalPDFUrl && (
                          <a
                            href={approvalPDFUrl}
                            download="Approval_Letter.pdf"
                            className="app-view-btn success-link"
                          >
                            Download Approval Letter
                          </a>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <label>{fileUploadLabel()}</label>
                      <input type="file" multiple onChange={handleFileChange} />
                    </>
                  )}

                  <label htmlFor="revision-outcome">If returning to applicant</label>
                  <select id="revision-outcome" value={revisionOutcome} onChange={e=>setRevisionOutcome(e.target.value)}>
                    {['REVISION_REQUIRED','MINOR_CORRECTIONS','MAJOR_CORRECTIONS','CONDITIONAL_APPROVAL','AWAITING_APPLICANT_RESPONSE'].map(value=><option key={value} value={value}>{value.replace(/_/g,' ')}</option>)}
                  </select>
                  <p className="apg-muted">These outcomes request an applicant response and return to this stage after resubmission.</p>
                  <div className="review-buttons">
                    <button
                      className="app-view-btn"
                      onClick={() => handleSubmit(false)}
                      disabled={
                        submitting ||
                        uploadingGeneratedDoc ||
                        (user?.userType === 'RESEARCH_CHAIR' && !ethicalClearancePDF) ||
                        (['ADMIN', 'PROFESSOR'].includes(user?.userType || '') && !approvalPDF)
                      }
                      title={submitting || uploadingGeneratedDoc ? 'Submitting…' : undefined}
                      aria-busy={submitting || uploadingGeneratedDoc}
                    >
                      {submitting ? 'Submitting...' : actionLabel}
                    </button>

                    <button
                      className="app-view-btn danger"
                      onClick={() => handleSubmit(true)}
                      disabled={submitting || uploadingGeneratedDoc}
                      title={submitting || uploadingGeneratedDoc ? 'Submitting…' : undefined}
                      aria-busy={submitting || uploadingGeneratedDoc}
                    >
                      {submitting ? 'Submitting...' : 'Request applicant response'}
                    </button>

                    <button
                      className="app-view-btn"
                      onClick={() => {
                        resetGeneratedDocs();
                        setSelectedApp(null);
                      }}
                      disabled={submitting || uploadingGeneratedDoc}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="view-section">
                  <button
                    className="app-view-btn"
                    onClick={() => {
                      resetGeneratedDocs();
                      setSelectedApp(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </ModalLayer>
      )}

      {successMessage && (
        <ModalLayer className="success-popup-overlay">
          <div className="success-popup-content">
            <p>{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)}>OK</button>
          </div>
        </ModalLayer>
      )}

      {errorMessage && (
        <ModalLayer className="success-popup-overlay">
          <div className="success-popup-content approval-error-popup" role="alert">
            <p>{errorMessage}</p>
            <button onClick={() => setErrorMessage(null)}>OK</button>
          </div>
        </ModalLayer>
      )}
    </main>
  );
};

export default ApprovalInbox;



